import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead, { PAGE_SEO } from "@/components/SEOHead";
import { Cpu, Tractor, Leaf, BarChart3, Wifi, Zap, ExternalLink, Factory, FlaskConical, Sprout, Clock, ChevronRight, Newspaper, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

const TechnologyPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchTechArticles = async () => {
      try {
        // Fetch agri-tech articles, excluding non-English regions (Brazil, France, India, China)
        const response = await fetch(`${API}/api/articles?category=agri-tech&exclude_regions=brazil,france,india,china&limit=18`);
        if (response.ok) {
          const data = await response.json();
          setArticles(data);
        }
      } catch (error) {
        console.error("Error fetching tech articles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTechArticles();
  }, []);
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-IE", { day: "numeric", month: "short" });
  };

  const resources = [
    {
      name: "Teagasc",
      description: "Ireland's agriculture and food development authority. Research, advisory services, and innovation.",
      url: "https://www.teagasc.ie",
      region: "Ireland",
      icon: Leaf,
      tags: ["Research", "Advisory", "Innovation"]
    },
    {
      name: "AgriSearch",
      description: "Farmer-funded research organization delivering practical farm trials and innovation projects.",
      url: "https://www.agrisearch.org",
      region: "Ireland/UK",
      icon: BarChart3,
      tags: ["Research", "Trials", "Data"]
    },
    {
      name: "AHDB",
      description: "Agriculture and Horticulture Development Board - UK's levy board for agriculture sectors.",
      url: "https://ahdb.org.uk",
      region: "UK",
      icon: BarChart3,
      tags: ["Market Data", "Research", "Benchmarking"]
    },
    {
      name: "Agri-TechE",
      description: "UK agri-tech innovation cluster connecting startups, researchers, and farmers.",
      url: "https://www.agri-tech-e.co.uk",
      region: "UK",
      icon: Cpu,
      tags: ["Startups", "Innovation", "AI"]
    },
    {
      name: "AgTechUCD",
      description: "UCD accelerator facilitating agri-tech startup growth in Ireland.",
      url: "https://www.ucd.ie/innovation/",
      region: "Ireland",
      icon: Sprout,
      tags: ["Startups", "Accelerator", "Innovation"]
    },
    {
      name: "UK Agritech Centre",
      description: "Supporting scaling innovations through industry networking and funding.",
      url: "https://www.agritechcentre.com",
      region: "UK",
      icon: Factory,
      tags: ["Funding", "Networking", "Scale-up"]
    },
    {
      name: "EMBRAPA",
      description: "Brazilian Agricultural Research Corporation - leading tropical agriculture innovation.",
      url: "https://www.embrapa.br/en",
      region: "Brazil",
      icon: Leaf,
      tags: ["Tropical", "Research", "Innovation"]
    },
    {
      name: "ICAR India",
      description: "Indian Council of Agricultural Research - coordinating agricultural research across India.",
      url: "https://icar.org.in",
      region: "India",
      icon: BarChart3,
      tags: ["Research", "Extension", "Education"]
    },
    {
      name: "CAAS China",
      description: "Chinese Academy of Agricultural Sciences - leading agricultural research institution.",
      url: "http://www.caas.cn/en",
      region: "China",
      icon: Cpu,
      tags: ["Research", "Technology", "Innovation"]
    }
  ];

  const techCategories = [
    {
      id: "precision",
      title: "Precision Agriculture",
      description: "Uses satellite imagery, drones, and GPS-guided machinery to optimize crop yields and reduce waste.",
      icon: Tractor,
      examples: [
        { name: "Satellite imagery", url: "https://www.esa.int/Applications/Observing_the_Earth/Copernicus/Sentinel-2" },
        { name: "Drone mapping", url: "https://www.dji.com/agriculture" },
        { name: "GPS guidance", url: "https://www.trimble.com/en/solutions/agriculture" },
        { name: "Variable rate technology", url: "https://www.teagasc.ie/crops/crops/precision-agriculture/" },
        { name: "Yield mapping", url: "https://www.johndeere.com/en/agriculture/precision-ag-technology/" }
      ]
    },
    {
      id: "iot",
      title: "Smart Farming & IoT",
      description: "Sensor networks monitor soil conditions, irrigation needs, and livestock health in real-time.",
      icon: Wifi,
      examples: [
        { name: "Soil sensors", url: "https://www.teagasc.ie/crops/soil--soil-fertility/soil-analysis/" },
        { name: "Weather stations", url: "https://www.met.ie/climate/services/agri-meteorological-services" },
        { name: "Livestock trackers", url: "https://moocall.com/" },
        { name: "Irrigation automation", url: "https://www.netafim.com/en/digital-farming/" },
        { name: "Real-time monitoring", url: "https://www.herdwatch.com/" }
      ]
    },
    {
      id: "cea",
      title: "Controlled Environment Agriculture",
      description: "Vertical farming, hydroponics, and aeroponics are used for year-round indoor growing.",
      icon: Factory,
      examples: [
        { name: "Vertical farming", url: "https://www.plenty.ag/" },
        { name: "Hydroponics", url: "https://www.teagasc.ie/rural-economy/rural-development/diversification/protected-crops/" },
        { name: "Aeroponics", url: "https://aerofarms.com/" },
        { name: "LED grow lights", url: "https://www.signify.com/en-gb/brands/philips-horticulture" },
        { name: "Climate control", url: "https://www.priva.com/horticulture" }
      ]
    },
    {
      id: "ai",
      title: "AI and Automation",
      description: "AI-powered algorithms predict disease outbreaks and pests, while robots handle harvesting and weeding.",
      icon: Cpu,
      examples: [
        { name: "Disease prediction", url: "https://plantix.net/" },
        { name: "Pest detection", url: "https://www.trapview.com/" },
        { name: "Robotic harvesting", url: "https://www.dogtooth.tech/" },
        { name: "Automated weeding", url: "https://carbonrobotics.com/" },
        { name: "Yield forecasting", url: "https://www.gro-intelligence.com/" }
      ]
    },
    {
      id: "proteins",
      title: "Alternative Proteins",
      description: "Development of plant-based and lab-grown meat to create more sustainable protein sources.",
      icon: FlaskConical,
      examples: [
        { name: "Plant-based meat", url: "https://www.beyondmeat.com/" },
        { name: "Cultured meat", url: "https://www.upside-foods.com/" },
        { name: "Insect protein", url: "https://www.hexafly.com/" },
        { name: "Fermentation", url: "https://www.perfectday.com/" },
        { name: "Novel ingredients", url: "https://gfi.org/" }
      ]
    },
    {
      id: "sustainability",
      title: "Sustainability Tech",
      description: "Technology for environmental monitoring, carbon tracking, and regenerative practices.",
      icon: Leaf,
      examples: [
        { name: "Carbon calculators", url: "https://www.carbontrust.com/our-work-and-impact/guides-reports-and-tools" },
        { name: "Biodiversity monitoring", url: "https://www.allbirds.ie/" },
        { name: "Renewable energy", url: "https://www.seai.ie/business-and-public-sector/farms/" },
        { name: "Water management", url: "https://www.teagasc.ie/environment/water-quality/" },
        { name: "Soil health", url: "https://www.teagasc.ie/crops/soil--soil-fertility/" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFF1E5]" data-testid="agri-tech-page">
      <SEOHead {...PAGE_SEO.agriTech} url="/agri-tech" />
      <Header />
      
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <Zap className="w-8 h-8 text-[#0F766E]" />
            <h1 className="font-serif text-3xl sm:text-4xl text-[#33302E]">Agri-Tech News & Innovation</h1>
          </div>
          <p className="text-base text-[#6B6661] max-w-3xl">
            The latest news on precision agriculture, farm robotics, AI in farming, vertical farming, 
            and agricultural technology startups from around the world.
          </p>
        </div>

        {/* LIVE AGRI-TECH NEWS FEED - Now prominently at top */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1 bg-[#DC2626] text-white text-xs font-mono uppercase tracking-wider">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                Live Feed
              </div>
              <h2 className="font-serif text-2xl text-[#33302E]">Latest Agri-Tech News</h2>
            </div>
            <Link 
              to="/?category=agri-tech" 
              className="text-sm text-[#0F766E] hover:text-[#064E3B] font-medium flex items-center gap-1"
            >
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white border border-[#E0D4C8] p-5 animate-pulse">
                  <div className="h-3 bg-[#E5E7EB] w-1/4 mb-4"></div>
                  <div className="h-5 bg-[#E5E7EB] w-3/4 mb-3"></div>
                  <div className="h-4 bg-[#E5E7EB] w-full mb-2"></div>
                  <div className="h-4 bg-[#E5E7EB] w-2/3"></div>
                </div>
              ))}
            </div>
          ) : articles.length > 0 ? (
            <>
              {/* Featured Article - First one larger */}
              <div className="grid lg:grid-cols-3 gap-6 mb-6">
                <div className="lg:col-span-2">
                  <Link
                    to={`/article/${articles[0]?.id}`}
                    className="block bg-gradient-to-br from-[#022C22] to-[#064E3B] text-white p-6 sm:p-8 hover:from-[#064E3B] hover:to-[#0F766E] transition-all group h-full"
                    data-testid="featured-tech-article"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-2 py-1 bg-[#0F766E] text-xs font-mono uppercase tracking-wider">Featured</span>
                      <span className="text-xs text-[#A7F3D0]">{formatDate(articles[0]?.published_at)}</span>
                      <span className="text-xs text-[#A7F3D0]">• {articles[0]?.source}</span>
                    </div>
                    <h3 className="font-serif text-2xl sm:text-3xl mb-4 group-hover:text-[#A7F3D0] transition-colors">
                      {articles[0]?.title}
                    </h3>
                    <p className="text-[#A7F3D0] line-clamp-3 mb-4">{articles[0]?.summary}</p>
                    <div className="flex items-center gap-2 text-sm text-white/80 group-hover:text-white">
                      <Newspaper className="w-4 h-4" />
                      Read Full Story
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                </div>
                
                {/* Second featured article */}
                {articles[1] && (
                  <Link
                    to={`/article/${articles[1]?.id}`}
                    className="block bg-white border border-[#E0D4C8] p-5 hover:border-[#0F766E] hover:shadow-md transition-all group"
                    data-testid={`tech-article-${articles[1]?.id}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="w-4 h-4 text-[#0F766E]" />
                      <span className="text-xs font-mono text-[#0F766E] uppercase">Trending</span>
                    </div>
                    <h3 className="font-serif text-lg text-[#33302E] mb-3 line-clamp-3 group-hover:text-[#0F766E] transition-colors">
                      {articles[1]?.title}
                    </h3>
                    <p className="text-sm text-[#6B6661] line-clamp-4 mb-4">{articles[1]?.summary}</p>
                    <div className="flex items-center gap-2 text-xs text-[#6B6661] mt-auto">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(articles[1]?.published_at)}</span>
                      <span>•</span>
                      <span className="truncate">{articles[1]?.source}</span>
                    </div>
                  </Link>
                )}
              </div>
              
              {/* News Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {articles.slice(2, 14).map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="bg-white border border-[#E0D4C8] p-5 hover:border-[#0F766E] hover:shadow-sm transition-all group"
                    data-testid={`tech-article-${article.id}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[10px] font-mono text-[#0F766E] uppercase px-2 py-0.5 bg-[#F0FDF4]">
                        {article.region || "Global"}
                      </span>
                      <span className="text-xs text-[#6B6661]">{formatDate(article.published_at)}</span>
                    </div>
                    <h3 className="font-serif text-base text-[#33302E] mb-2 line-clamp-2 group-hover:text-[#0F766E] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-[#6B6661] line-clamp-2 mb-3">{article.summary}</p>
                    <div className="flex items-center text-xs text-[#8B8580]">
                      <span className="truncate">{article.source}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : (
            <div className="bg-white border border-[#E0D4C8] p-8 text-center">
              <Cpu className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
              <p className="text-[#6B6661] mb-2">No agri-tech articles yet.</p>
              <p className="text-sm text-[#8B8580]">Fresh content is being scraped - check back soon!</p>
            </div>
          )}
        </section>

        {/* Divider */}
        <div className="border-t border-[#E0D4C8] my-10"></div>

        {/* Tech Categories Grid */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl text-[#33302E] mb-6">Key Areas of Agri-Tech Innovation</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techCategories.map((cat) => (
              <button 
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? "all" : cat.id)}
                className={`bg-white border p-6 text-left transition-all ${
                  selectedCategory === cat.id 
                    ? "border-[#0F766E] bg-[#F0FDF4]" 
                    : "border-[#E0D4C8] hover:border-[#0F766E]"
                }`}
              >
                <cat.icon className="w-10 h-10 text-[#0F766E] mb-4" />
                <h3 className="font-serif text-lg text-[#33302E] mb-2">{cat.title}</h3>
                <p className="text-sm text-[#6B6661] mb-4">{cat.description}</p>
                <ul className="space-y-1">
                  {cat.examples.map((ex) => (
                    <li key={`${cat.id}-${ex.name}`} className="text-xs flex items-center gap-2">
                      <span className="w-1 h-1 bg-[#0F766E] rounded-full flex-shrink-0"></span>
                      <a 
                        href={ex.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-[#0F766E] hover:text-[#064E3B] hover:underline flex items-center gap-1"
                      >
                        {ex.name}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </button>
            ))}
          </div>
        </section>

        {/* Regional Innovation Hubs */}
        <section className="mb-12">
          <h2 className="font-serif text-2xl text-[#33302E] mb-6">Regional Innovation Hubs & Resources</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource) => (
              <a
                key={resource.name}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white border border-[#E0D4C8] p-6 hover:border-[#0F766E] hover:shadow-sm transition-all group"
                data-testid={`resource-${resource.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <resource.icon className="w-8 h-8 text-[#0F766E]" />
                  <span className="text-xs font-mono text-[#6B6661] uppercase">{resource.region}</span>
                </div>
                <h3 className="font-serif text-lg text-[#33302E] mb-2 group-hover:text-[#0F766E] transition-colors flex items-center gap-2">
                  {resource.name}
                  <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </h3>
                <p className="text-sm text-[#6B6661] mb-4">{resource.description}</p>
                <div className="flex flex-wrap gap-2">
                  {resource.tags.map((tag) => (
                    <span key={`${resource.name}-${tag}`} className="text-xs px-2 py-1 bg-[#F6E9DD] text-[#6B6661]">
                      {tag}
                    </span>
                  ))}
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#0F766E] text-white p-8">
          <h2 className="font-serif text-2xl mb-4">Stay Ahead of Agri-Tech Innovation</h2>
          <p className="text-[#A7F3D0] mb-6">
            Get the latest farming technology news, research updates, and innovation insights delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="/dashboard" 
              className="px-6 py-3 bg-white text-[#0F766E] font-medium text-center hover:bg-[#F6E9DD] transition-colors"
            >
              Set Up Agri-Tech Alerts
            </a>
            <a 
              href="/" 
              className="px-6 py-3 border border-white text-white font-medium text-center hover:bg-white/10 transition-colors"
            >
              Browse All News
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TechnologyPage;
